import React from "react";
import { useTheme } from "../../../../contexts/ThemeContext";

interface ConfirmActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText: string;
  cancelText?: string;
  type?: "delete" | "action";
}

const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText = "Maybe later",
  type = "action",
}) => {
  const { theme } = useTheme();

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
        theme === "dark" ? "bg-black/60" : "bg-black/40"
      }`}
    >
      <div
        className={`relative w-full max-w-md rounded-2xl shadow-xl border ${
          theme === "dark"
            ? "bg-[#1E1E1E] text-gray-100 border-[#2C2C2C]"
            : "bg-white text-gray-900 border-gray-200"
        }`}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className={`absolute top-3 right-3 p-2 rounded-full ${
            theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"
          }`}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
          </svg>
        </button>

        <div className="px-6 py-6 text-center">
          <div
            className={`mx-auto mb-3 flex items-center justify-center w-12 h-12 rounded-full ${
              theme === "dark" ? "bg-[#111111]" : "bg-gray-100"
            }`}
          >
            {type === "delete" ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#B6CB24">
                <path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1z" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M20 10C20.5523 10 21 10.4477 21 11V16C21 16.5523 20.5523 17 20 17H15C14.4477 17 14 16.5523 14 16C14 15.4477 14.4477 15 15 15H19V11C19 10.4477 19.4477 10 20 10Z"
                  fill="#C6D92C"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3.29289 7.29289C3.68342 6.90237 4.31658 6.90237 4.70711 7.29289L9 11.5858L12.2929 8.29289C12.6834 7.90237 13.3166 7.90237 13.7071 8.29289L20.7071 15.2929C21.0976 15.6834 21.0976 16.3166 20.7071 16.7071C20.3166 17.0976 19.6834 17.0976 19.2929 16.7071L13 10.4142L9.70711 13.7071C9.31658 14.0976 8.68342 14.0976 8.29289 13.7071L3.29289 8.70711C2.90237 8.31658 2.90237 7.68342 3.29289 7.29289Z"
                  fill="#C6D92C"
                />
              </svg>
            )}
          </div>
          <h3 className="text-lg font-semibold mb-2">{title}</h3>
          <p
            className={`${
              theme === "dark" ? "text-gray-300" : "text-gray-600"
            } mb-6`}
          >
            {message}
          </p>

          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={onConfirm}
              className="px-4 py-2 font-medium hover:opacity-90 text-white rounded-[10px] border"
              style={{
                border:
                  type === "delete"
                    ? "1px solid #D92C2C"
                    : "1px solid var(--menu-active-border, #C6D92C)",
                background:
                  type === "delete"
                    ? "radial-gradient(94.17% 100% at 52.48% 0%, rgba(217, 44, 44, 0.80) 0%, rgba(91, 16, 16, 0.80) 100%)"
                    : "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
              }}
            >
              {confirmText}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 font-medium hover:opacity-90 text-white rounded-[10px] border"
              style={{
                border:
                  type === "delete"
                    ? "none outline-none"
                    : "1px solid  #9B9DB6",

                background:
                  type === "delete"
                    ? "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)"
                    : "radial-gradient(94.17% 100% at 52.48% 0%, rgba(46, 46, 46, 0.80) 0%, rgba(71, 71, 71, 0.80) 100%)",
              }}
            >
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmActionModal;
